/**
 * Pagina de gestao de chaves de acesso (passkeys) do usuario logado.
 * Rota: /dashboard/chaves
 *
 * - Lista as passkeys cadastradas.
 * - Permite adicionar novas (fluxo autenticado, sem convite).
 * - Permite renomear e revogar.
 * - Backend impede revogar a ultima passkey — mostramos erro amigavel.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from "@payflow/ui";
import {
  deletePasskey,
  isPasskeySupported,
  listPasskeys,
  registerPasskey,
  renamePasskey,
  suggestPasskeyNickname,
  type Passkey,
} from "../../services/webauthn.service";

export function PasskeysPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(true);
  const supported = isPasskeySupported();

  // Add
  const [addOpen, setAddOpen] = useState(false);
  const [addNickname, setAddNickname] = useState("");
  const [adding, setAdding] = useState(false);
  // Snapshot inicial do nickname pra prompt de alteracoes nao salvas.
  const addInitialRef = useRef<string>("");

  // Rename
  const [renameTarget, setRenameTarget] = useState<Passkey | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<Passkey | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPasskeys = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPasskeys();
      setPasskeys(data);
    } catch (err) {
      toast({
        variant: "error",
        title: err instanceof Error ? err.message : t("passkeys.load_error"),
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchPasskeys();
  }, [fetchPasskeys]);

  const openAdd = () => {
    const initial = suggestPasskeyNickname();
    setAddNickname(initial);
    addInitialRef.current = initial;
    setAddOpen(true);
  };

  const handleAdd = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!addNickname.trim()) {
      toast({ variant: "error", title: t("passkeys.error_nickname_required") });
      return;
    }
    setAdding(true);
    try {
      await registerPasskey(addNickname.trim());
      toast({ variant: "success", title: t("passkeys.add_success") });
      setAddOpen(false);
      setAddNickname("");
      fetchPasskeys();
    } catch (err) {
      toast({
        variant: "error",
        title: mapError(err, t, t("passkeys.add_error")),
      });
    } finally {
      setAdding(false);
    }
  };

  const openRename = (p: Passkey) => {
    setRenameTarget(p);
    setRenameValue(p.nickname);
  };

  const handleRename = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!renameTarget || !renameValue.trim()) return;
    setRenaming(true);
    try {
      await renamePasskey(renameTarget.id, renameValue.trim());
      toast({ variant: "success", title: t("passkeys.rename_success") });
      setRenameTarget(null);
      fetchPasskeys();
    } catch (err) {
      toast({
        variant: "error",
        title: mapError(err, t, t("passkeys.rename_error")),
      });
    } finally {
      setRenaming(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePasskey(deleteTarget.id);
      toast({ variant: "success", title: t("passkeys.delete_success") });
      setDeleteTarget(null);
      fetchPasskeys();
    } catch (err) {
      toast({
        variant: "error",
        title: mapError(err, t, t("passkeys.delete_error")),
      });
    } finally {
      setDeleting(false);
    }
  };

  const locale = i18n.language || "pt-BR";
  const formatDate = (iso: string | null) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString(locale);
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("passkeys.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("passkeys.subtitle")}
          </p>
        </div>
        {supported && (
          <Button onClick={openAdd}>
            <PlusIcon />
            {t("passkeys.add")}
          </Button>
        )}
      </div>

      {!supported && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
          <p className="font-semibold text-warning">
            {t("auth.passkey.unsupported_title")}
          </p>
          <p className="mt-1 text-muted-foreground">
            {t("auth.passkey.unsupported_message")}
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("passkeys.list_title")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
            </div>
          ) : passkeys.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {t("passkeys.empty")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left">
                    <th className="px-4 py-3 font-medium text-muted-foreground">
                      {t("passkeys.col_nickname")}
                    </th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">
                      {t("passkeys.col_type")}
                    </th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">
                      {t("passkeys.col_created")}
                    </th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">
                      {t("passkeys.col_last_used")}
                    </th>
                    <th className="w-32 px-4 py-3 text-right font-medium text-muted-foreground">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {passkeys.map((p) => (
                    <tr key={p.id} className="border-b border-border">
                      <td className="px-4 py-3 font-medium text-foreground">
                        {p.nickname}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="default" size="md">
                          {p.deviceType || t("passkeys.type_unknown")}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatDate(p.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatDate(p.lastUsedAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRename(p)}
                            className="h-8 w-8 p-0"
                            aria-label={t("passkeys.rename")}
                          >
                            <EditIcon />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(p)}
                            className="h-8 w-8 p-0 text-error hover:text-error"
                            aria-label={t("passkeys.revoke")}
                          >
                            <TrashIcon />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={setAddOpen}
        dirty={addNickname !== addInitialRef.current}
        onSave={async () => { await handleAdd(); }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{t("passkeys.add_title")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("passkeys.add_message")}
            </p>
            <Input
              label={t("passkeys.nickname_label")}
              value={addNickname}
              onChange={(e) => setAddNickname(e.target.value)}
              placeholder={t("passkeys.nickname_placeholder")}
              required
              autoFocus
            />
            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => setAddOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={adding}>
                {t("passkeys.add_confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => !open && setRenameTarget(null)}
        dirty={!!renameTarget && renameValue !== renameTarget.nickname}
        onSave={async () => { await handleRename(); }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{t("passkeys.rename_title")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="space-y-4">
            <Input
              label={t("passkeys.nickname_label")}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              required
              autoFocus
            />
            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => setRenameTarget(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={renaming}>
                {t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("passkeys.delete_title")}
        description={t("passkeys.delete_message", {
          nickname: deleteTarget?.nickname,
        })}
        confirmLabel={t("passkeys.revoke")}
        cancelLabel={t("common.cancel")}
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function mapError(
  err: unknown,
  t: (key: string) => string,
  fallback: string
): string {
  const code = err instanceof Error ? err.message : "";
  switch (code) {
    case "PASSKEY_CANCELED":
      return t("auth.passkey.error_canceled");
    case "PASSKEY_UNSUPPORTED":
      return t("auth.passkey.error_unsupported");
    case "PASSKEY_ALREADY_REGISTERED":
      return t("auth.passkey.error_already_registered");
    default:
      return code || fallback;
  }
}

function PlusIcon() {
  return (
    <svg
      className="mr-2 h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4v16m8-8H4"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}
